import { z } from 'zod';

import { emailSchema, isoDateTimeSchema, ulidSchema, userIdSchema } from './primitives';

export const reviewStarsSchema = z.number().int().min(1).max(5);
export type ReviewStars = z.infer<typeof reviewStarsSchema>;

export const reviewStatusSchema = z.enum(['PUBLISHED', 'HIDDEN_BY_ADMIN']);
export type ReviewStatus = z.infer<typeof reviewStatusSchema>;

export const restaurantResponseEntitySchema = z.object({
  body: z.string().min(1).max(2000),
  respondedAt: isoDateTimeSchema,
  /** Cognito `sub` of the staff member who posted the response. */
  respondedByUserId: z.string().min(1).max(128),
  editedAt: isoDateTimeSchema.optional(),
});

export const reviewEntitySchema = z.object({
  orderId: ulidSchema,
  reviewId: ulidSchema,
  restaurantId: ulidSchema,
  customerUserId: userIdSchema.optional(),
  guestEmail: emailSchema.optional(),
  customerDisplayName: z.string().min(1).max(140),
  stars: reviewStarsSchema,
  body: z.string().max(2000).optional(),
  submittedAt: isoDateTimeSchema,
  editedAt: isoDateTimeSchema.optional(),
  restaurantResponse: restaurantResponseEntitySchema.optional(),
  status: reviewStatusSchema,
  hiddenReason: z.string().optional(),
  hiddenByAdminUserId: ulidSchema.optional(),
  adminFlag: z
    .object({
      flaggedAt: isoDateTimeSchema,
      flaggedByUserId: ulidSchema,
      reason: z.string().min(10).max(1000),
    })
    .optional(),
  /** Sparse: 'FLAGGED' iff adminFlag is set and status === 'PUBLISHED'. Powers GSI3. */
  adminFlagStatus: z.enum(['FLAGGED']).optional(),
});
export type ReviewEntity = z.infer<typeof reviewEntitySchema>;

export const reviewPublicSchema = z.object({
  reviewId: ulidSchema,
  customerDisplayName: z.string(),
  stars: reviewStarsSchema,
  body: z.string().optional(),
  submittedAt: isoDateTimeSchema,
  editedAt: isoDateTimeSchema.optional(),
  wasOrderRefunded: z.boolean(),
  restaurantResponse: z
    .object({
      body: z.string(),
      respondedAt: isoDateTimeSchema,
      editedAt: isoDateTimeSchema.optional(),
    })
    .optional(),
});
export type ReviewPublic = z.infer<typeof reviewPublicSchema>;

export const reviewOwnerSchema = reviewPublicSchema.extend({
  status: reviewStatusSchema,
  hiddenReason: z.string().optional(),
  restaurantResponse: restaurantResponseEntitySchema.optional(),
  adminFlag: z
    .object({
      flaggedAt: isoDateTimeSchema,
      flaggedByUserId: ulidSchema,
      reason: z.string(),
    })
    .optional(),
});
export type ReviewOwner = z.infer<typeof reviewOwnerSchema>;

export const reviewAdminSchema = reviewOwnerSchema.extend({
  orderId: ulidSchema,
  restaurantId: ulidSchema,
  customerUserId: userIdSchema.optional(),
  guestEmail: emailSchema.optional(),
  hiddenByAdminUserId: ulidSchema.optional(),
});
export type ReviewAdmin = z.infer<typeof reviewAdminSchema>;

export const reviewSelfSchema = reviewPublicSchema.extend({
  editable: z.boolean(),
});
export type ReviewSelf = z.infer<typeof reviewSelfSchema>;
